import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CredentialsService } from './credentials.service';
import { DeepLinkingService } from '@shared/services/deep-linking.service';
import { AuthenticationService } from './authentication.service';

const USERNAME_QUERY_PARAM = 'username';
const TEASER_QUERY_PARAM = 't';
/**
 * Guard to check if user has permissions to enter a given route
 */
@Injectable(
  {
    providedIn: 'root',
  },
)
export class AuthenticationGuard implements CanActivate {

  constructor(
    private readonly router: Router,
    private readonly credentialsService: CredentialsService,
    private readonly deepLinkingService: DeepLinkingService,
    private readonly authService: AuthenticationService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (state.url.includes('logout') || state.url.includes('token') || !environment.usesExternalAPI) {
      return true;
    }

    // Get any teaser ID and remove the query parameter.
    // Teaser ID will be null if it doesn't exist
    const username: string = route.queryParamMap.get(USERNAME_QUERY_PARAM);
    const teaserId: string = route.queryParamMap.get(TEASER_QUERY_PARAM);
    // A URL base is used to prevent errors but isn't used in the final updated URL.
    const url: URL = new URL(state.url, 'https://base-not-used');
    const navState = this.router.getCurrentNavigation()?.extras?.state;

    const updatedUrl: string = url.pathname + url.search + url.hash;
    if (this.credentialsService.isAuthenticated()) {
      // https://techinsights.atlassian.net/browse/CP-17995 UUI async startup problems tech debt
      // Refactor this weirdness.
      // shouldRedirect returns an Observable which will always evaluate to true,
      // but it executes some code needed by redirect so this check can't just be removed.
      if (this.deepLinkingService.shouldRedirect(updatedUrl)) {
        this.deepLinkingService.redirect(updatedUrl);
      }
      return true;
    } else {
      // If the user is not authenticated, save the destination URL to redirect to after authentication.
      // If the URL contained a username, skip authentication page
      // Once user logs in they will go directly to the original destination URL.
      this.deepLinkingService.setRedirectionUrl(updatedUrl);
      //if the user is not authenticated, but has an email or username, log them in
      const email = this.credentialsService.getOauth()?.email;
      if (!!username || !!email){
        this.authService.loginUser(username??email);
      } else if (!!teaserId && (teaserId === 'search')) {
        // User is not authenticated, but with a teaser ID they are allowed to go to the public route
        this.router.navigateByUrl(`/public/${teaserId}${url.search}${url.hash}`);
      } else if(teaserId) {
        this.router.navigateByUrl(`/public/wp-blog/${teaserId}${url.search}${url.hash}`);
      } else {
        this.router.navigateByUrl(`/public${updatedUrl}`, { state: navState });
      }
      return false;
    }
  }
}
